import { useEffect, useId, useLayoutEffect, useRef, useCallback } from "react";

import { FloatingPortal } from "@floating-ui/react";
import { observer } from "mobx-react-lite";

import { getSymbolFontContext } from "@/components/expression/utils/getSymbolFontContext";
import * as Node from "@/components/expression/utils/layout";
import { useSymbolPushTailwindClassName } from "@/components/expression/utils/layout/useSymbolPushTailwindClassName";
import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { commitPendingInput } from "@/components/input/utils/commitPendingInput";
import { splitInlineExpression } from "@/components/input/utils/splitInlineExpression";
import { SUGGESTIONS_MENU_ATTR } from "@/components/suggestions/constants";
import {
  suggestionOptionId,
  suggestionSubmenuId,
} from "@/components/suggestions/navigation/SuggestionsListProvider";
import { handlePickSuggestion } from "@/components/suggestions/useSuggestions";
import { useSuggestionsFloating } from "@/components/suggestions/useSuggestionsFloating";
import { topUndoIsAutoMathConversion } from "@/components/tiptap/Collaboration";
import { removeEmptyInlineExpressionIfNeeded } from "@/components/tiptap/tiptap-expression/utils";
import { getKeyboardInputShortcut, triggerKeyboardInput } from "@/input/handleKeyboardInput";
import { deleteNode, deleteSelectedNodes } from "@/input/utils/editOperators";
import { findSelectionApplyOperator } from "@/input/utils/findSelectionApplyOperator";
import { getCaretPositionFromCoordinates } from "@/input/utils/getCaretPositionFromCoordinates";
import { inputSubSup } from "@/input/utils/inputSubSup";
import { cursorIsActive, CURSOR_POSITION_CLASS_NAME } from "@/models/CursorModel";
import { type DocumentModel } from "@/models/DocumentModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import { hasUnit } from "@/models/nodes/utils";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import {
  cancelImeRecommit,
  isReplayableComposedChar,
  MATH_INPUT_ATTR,
  replayKeydown,
} from "@/utils/AndroidSymbolKeydownReplay";
import { getIsAndroid } from "@/utils/getIsAndroid";
import { getIsIOS } from "@/utils/getIsIOS";
import { getIsMobile, isMobileWebViewUserAgent } from "@/utils/getIsMobile";
import { isOpenSuggestionsShortcut } from "@/utils/hotkeys";
import { isSymbolKey } from "@/utils/input";
import { keyMatcher } from "@/utils/keyMatcher";
import { normalizeSymbols } from "@/utils/normalizeSymbols";
import { matchSubSupChar } from "@/utils/normalizeUnicodeSubSup";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import { Suggestions } from "../suggestions/Suggestions";
import { useSuggestionsContext } from "../suggestions/SuggestionsProvider";
import { searchSuggestions, splitInlineEditable } from "../suggestions/utils";
import {
  escapeUnitForDisallowedInput,
  isCharAllowedInUnit,
} from "../suggestions/utils/isAllowedInUnit";

import { useCursorContext } from "./CursorContextProvider";
import { InputWithMath } from "./InputWithMath";
import { deleteInlineEditableNode } from "./utils/deleteInlineEditableNode";
import { mergeInlineEditables } from "./utils/mergeInlineEditables";

import type { Dimension } from "@/components/expression/utils/layout/NodeDimensions";
import type { Suggestion } from "@/components/suggestions/types";
import type { NodeModel } from "@/models/nodes/types";

/** Autocorrect replacements fired this soon after a keydown come from that keystroke, not a tap. */
const KEYSTROKE_REPLACEMENT_WINDOW_MS = 100;

type NodeKeyboardInputProps = {
  doc: DocumentModel;
  node?: NodeModel;
  className?: string;
  inputClassName?: string;
  disableEditingStyle?: boolean;
};

export const NodeKeyboardInput = observer(function NodeKeyboardInput({
  doc,
  node,
  className,
  inputClassName,
  disableEditingStyle,
}: NodeKeyboardInputProps) {
  const { cursor } = doc;
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    id: cursorContextId,
    isFocused: contextIsFocused,
    setIsFocused: setContextIsFocused,
  } = useCursorContext();

  // Focus here rather than via autoFocus, so InputModel owns it and can pass preventScroll.
  // Layout effect to match autoFocus's timing — before the browser paints the moved cursor.
  useLayoutEffect(() => {
    doc.input.registerInputRef(inputRef);
    doc.input.focus({ caretAtEnd: true });

    return () => {
      doc.input.unregisterInputRef();
    };
  }, [doc]);

  // iOS shows its suggestion bar only with autocorrect on. Keep it in the app so the keyboard
  // height (and the native toolbar above it) doesn't change between text and math.
  const showKeyboardSuggestions = getIsIOS() && isMobileWebViewUserAgent(navigator.userAgent);
  const lastKeyDownAtRef = useRef(0);

  useEffect(() => {
    const input = inputRef.current;
    if (!input || !showKeyboardSuggestions) {
      return;
    }

    // A tapped suggestion is inserted as plain text after leaving math; autocorrect rewrites
    // triggered by typing are dropped so they can't mangle math tokens ("sinx" → "sink").
    const handleBeforeInput = (e: InputEvent) => {
      const isReplacement = e.inputType === "insertReplacementText";
      if (!isReplacement && e.inputType !== "insertText") {
        return;
      }

      const fromKeystroke =
        performance.now() - lastKeyDownAtRef.current < KEYSTROKE_REPLACEMENT_WINDOW_MS;
      if (fromKeystroke) {
        if (isReplacement) {
          e.preventDefault();
        }
        return;
      }

      // A suggestion tap has no keydown. Tapping the word already typed only inserts " ".
      // Other keydown-less text (dictation, handwriting) is regular input for the math.
      const isSuggestionTap = isReplacement || e.data === " ";
      if (!isSuggestionTap) {
        return;
      }

      e.preventDefault();
      const text = e.data ?? e.dataTransfer?.getData("text/plain") ?? "";
      const { value } = input;
      const start = input.selectionStart ?? value.length;
      const end = input.selectionEnd ?? value.length;
      const head = isReplacement
        ? value.slice(0, start).replace(/[\p{L}\p{N}]*$/u, "")
        : value.slice(0, start);
      const result = `${head}${text}${value.slice(end)}`;
      // Trailing space like a native suggestion tap; without it iOS treats the inserted word
      // as still being typed and the next suggestion replaces it instead of following it.
      splitInlineExpression(doc, result.endsWith(" ") ? result : `${result} `);
    };

    input.addEventListener("beforeinput", handleBeforeInput);
    return () => input.removeEventListener("beforeinput", handleBeforeInput);
  }, [doc, showKeyboardSuggestions]);

  const { view, text, size } = useNodeStyle();
  const symbolFontContext = getSymbolFontContext(view, size);
  const symbolPushClassName = useSymbolPushTailwindClassName(doc.input.value);
  const { suggestionsAnchorId, accept, suggestionsRenderPlace } = useSuggestionsContext();

  const isEditing = doc.input.isEditing(node);
  const isTextView = view === "text";
  const isParameterChild = text === "parameter";

  let inputDimensions: Dimension = "square";
  if (node?.is(OperatorSymbolNodeModel) || node?.is(VariableNodeModel)) {
    inputDimensions = "narrow";
  } else if (node?.is(NumberNodeModel)) {
    inputDimensions = "tight";
  }

  const handleKeyDown = (e: KeyboardEvent | React.KeyboardEvent<HTMLInputElement>): void => {
    lastKeyDownAtRef.current = performance.now();

    if (!cursorIsActive(cursor)) {
      return;
    }

    if (keyMatcher("Mod+KeyZ", e)) {
      if (doc.input.value.length > 0) {
        e.preventDefault();
        e.stopPropagation();

        // When the pending input is the char that just auto-converted text to math
        // (e.g. the "/" in "a/", queued for the math input), undo the conversion back
        // to the typed text in one step instead of only clearing the pending input.
        const tiptapState = doc.editor.tiptap?.state;
        if (tiptapState && topUndoIsAutoMathConversion(tiptapState)) {
          doc.input.reset(true);
          doc.editor.undo();
        } else {
          doc.editor.resetInput(true);
        }
        return;
      }
    }

    if (isOpenSuggestionsShortcut(e)) {
      if (doc.editor.suggestions.available) {
        e.preventDefault();
        e.stopPropagation();
        const context = doc.selection.isMathOnlySelection ? "math_node" : "caret";
        doc.editor.suggestions.open(context);
        trackEvent("suggestions_opened", { context, source: "shortcut", doc_id: doc.id });
      }
      return;
    }

    const inputValue = doc.input.value;

    const nativeEvent = "nativeEvent" in e ? e.nativeEvent : e;

    const selectLeft = keyMatcher("Shift+ArrowLeft", e);
    const selectRight = keyMatcher("Shift+ArrowRight", e);
    const selectHome = keyMatcher("Shift+Home", e);
    const selectEnd = keyMatcher("Shift+End", e);
    const { editingNode } = doc.input;

    let moveDirection = 0;
    if (keyMatcher("ArrowLeft", e) || selectLeft) {
      moveDirection = -1;
    } else if (keyMatcher("ArrowRight", e) || selectRight) {
      moveDirection = 1;
    }

    if (moveDirection !== 0 && e.target instanceof HTMLInputElement && e.target.value.length > 0) {
      const { selectionStart, selectionDirection, selectionEnd } = e.target;
      const selectionHead = selectionDirection === "backward" ? selectionStart : selectionEnd;

      const pos = (selectionHead ?? 0) + moveDirection;
      const shift = selectLeft || hasUnit(editingNode, "prefix") ? 0 : 1;

      if (pos >= shift && pos <= e.target.value.length) {
        doc.cursor.releaseLockLeft();
        return;
      }
    }

    if (editingNode && (selectLeft || selectRight || selectHome || selectEnd)) {
      doc.editor.resetInput(true);

      if (selectRight || selectEnd) {
        doc.cursor.moveBeforeNode(editingNode);
      } else {
        doc.cursor.moveAfterNode(editingNode);
      }

      doc.selection.selectAfterCursor(() => {
        if (selectLeft) {
          if (hasUnit(editingNode) || editingNode.is(FunctionNodeModel)) {
            doc.cursor.moveBeforeNode(editingNode);
          } else {
            doc.cursor.moveLeft();
          }
        } else if (selectRight) {
          if (hasUnit(editingNode) || editingNode.is(FunctionNodeModel)) {
            doc.cursor.moveAfterNode(editingNode);
          } else {
            doc.cursor.moveRight();
          }
        } else if (selectHome) {
          doc.cursor.moveToLineStart();
        } else {
          doc.cursor.moveToLineEnd();
        }
      });

      return;
    }

    if (keyMatcher("Escape", e)) {
      e.stopPropagation();
      e.preventDefault();

      if (doc.input.editingNode) {
        doc.editor.resetInput(true);
        return;
      }

      if (inputValue.length > 0 && doc.cursor.level === 0) {
        splitInlineExpression(doc);
      }

      doc.editor.resetInput();

      triggerKeyboardInput(doc, "Escape", nativeEvent);
      return;
    }

    const isSymbol = isSymbolKey(e);

    if (isSymbol && !doc.input.value && !isEditing) {
      escapeUnitForDisallowedInput(doc.cursor, isCharAllowedInUnit(e.key, doc.cursor.node));
    }

    if (
      !isEditing &&
      !doc.selection.hasSelectedNodes &&
      !doc.input.value &&
      isSymbol &&
      !(keyMatcher("Space", e) || keyMatcher("Shift+Space", e))
    ) {
      const nodeBefore = doc.cursor.nodeBeforeCursor;

      if (
        nodeBefore?.isInlineEditable() &&
        nodeBefore.isMergeable &&
        nodeBefore.validateValue(nodeBefore.value + e.key, false) &&
        !hasUnit(nodeBefore, "postfix") &&
        !(
          ("power" in nodeBefore && nodeBefore.power) ||
          ("subscript" in nodeBefore && nodeBefore.subscript)
        )
      ) {
        e.preventDefault();
        e.stopPropagation();
        doc.transact(() => {
          nodeBefore.value += e.key;
          mergeInlineEditables(doc, nodeBefore.prevSiblings[0], nodeBefore);
        }, "NodeKeyboardInput:updateNumber");
        return;
      }

      const nodeAfter = doc.cursor.nodeAfterCursor;
      if (
        nodeAfter?.isInlineEditable() &&
        nodeAfter.isMergeable &&
        nodeAfter.validateValue(e.key + nodeAfter.value, false) &&
        !hasUnit(nodeAfter, "prefix")
      ) {
        e.preventDefault();
        e.stopPropagation();
        doc.transact(() => {
          nodeAfter.value = e.key + nodeAfter.value;
          doc.input.editNode(nodeAfter, 1);
          mergeInlineEditables(doc, nodeAfter, nodeAfter.nextSiblings[0]);
        }, "NodeKeyboardInput:updateNumber");
        return;
      }
    }

    const shortcut = getKeyboardInputShortcut(e);
    let { activeSuggestion } = doc.editor.suggestions;

    // FIXME: this guard shouldn't be necessary, but in some cases (e.g. in storybook) the state can get out of sync
    // the state is too complex and needs to be simplified to avoid this
    if (doc.editor.suggestions.mode === "idle") {
      activeSuggestion = undefined;
    }

    const canPickSuggestion = shortcut?.canPickSuggestion
      ? shortcut.canPickSuggestion(doc, inputValue)
      : activeSuggestion && !isEditing && !doc.selection.hasSelectedNodes;

    if (shortcut) {
      const pickedSuggestion = canPickSuggestion ? activeSuggestion : undefined;
      let insertedNodes: NodeModel[] | null = null;

      if (pickedSuggestion) {
        insertedNodes = handlePickSuggestion(doc, pickedSuggestion, {
          preserveHints: true,
          selectedNode: node,
        });
      }

      const triggerMode = canPickSuggestion ? "after-autosuggest" : "regular";
      const actionTriggered = shortcut.action(doc, {
        event: "nativeEvent" in e ? e.nativeEvent : e,
        input: inputValue,
        triggerMode,
        insertedNodes: insertedNodes ?? undefined,
      });
      const preventDefault = shortcut.preventDefault
        ? shortcut.preventDefault(doc, inputValue, triggerMode)
        : false;

      if (preventDefault || actionTriggered) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }

    if (isSymbol && !doc.input.value && !doc.input.editingNode) {
      const suggestions = searchSuggestions(e.key, doc, []);

      // auto-pick suggestion if it's the only one hidden suggestion (e.g. a number)
      if (suggestions.length === 1 && suggestions[0].hideStandalone) {
        const matched = matchSubSupChar(e.key);

        if (matched) {
          inputSubSup(matched.position)(doc);
        }

        const insertedNodes = handlePickSuggestion(doc, suggestions[0], { preserveHints: false });

        if (insertedNodes && insertedNodes.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          doc.editor.resetInput(true);
        }
      }
    }
  };

  const handleSelect = useCallback(
    (suggestion: Suggestion, e?: KeyboardEvent, isSelectedManually = false) => {
      if (suggestion.type !== "single" && suggestion.type !== "multi") {
        handlePickSuggestion(doc, suggestion, { selectedNode: node });
        return;
      }

      if (
        e?.target instanceof HTMLInputElement &&
        keyMatcher("Space", e) &&
        node?.isInlineEditable() &&
        node.isSplittable &&
        doc.cursor.level === 0
      ) {
        e.preventDefault();
        e.stopPropagation();
        splitInlineEditable(doc, node, e.target, e.key);

        return;
      }

      if (!e || e.defaultPrevented) {
        handlePickSuggestion(doc, suggestion, {
          preserveHints: true,
          selectedNode: node,
          isSelectedManually,
        });
        return;
      }

      const shortcut = getKeyboardInputShortcut(e);

      if (!shortcut) {
        handlePickSuggestion(doc, suggestion, {
          preserveHints: true,
          selectedNode: node,
          isSelectedManually,
        });
        return;
      }

      const inputValue = doc.input.value;

      const canPickSuggestion = shortcut?.canPickSuggestion
        ? shortcut.canPickSuggestion(doc, inputValue)
        : !isEditing;

      let insertedNodes: NodeModel[] | null = null;

      const pickedSuggestion =
        canPickSuggestion && !doc.selection.hasSelectedNodes ? suggestion : undefined;
      if (pickedSuggestion) {
        insertedNodes = handlePickSuggestion(doc, pickedSuggestion, {
          preserveHints: true,
          selectedNode: node,
          isSelectedManually,
        });
      }

      const triggerMode = canPickSuggestion ? "after-autosuggest" : "regular";
      const actionTriggered = shortcut.action(doc, {
        event: e,
        input: inputValue,
        triggerMode,
        insertedNodes: insertedNodes ?? undefined,
      });

      const preventDefault = shortcut.preventDefault
        ? shortcut.preventDefault(doc, inputValue, triggerMode)
        : actionTriggered;

      if (preventDefault) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [doc, isEditing, node],
  );

  /** Replays against the still-unchanged pending value, as the desktop keydown would see it. */
  const replayAndroidComposedChar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { inputType } = e.nativeEvent as InputEvent;
    const prevValue = doc.input.value;
    const { value } = e.target;
    const char = value.slice(prevValue.length);

    if (
      !getIsAndroid() ||
      inputType !== "insertCompositionText" ||
      !prevValue ||
      doc.input.editingNode ||
      !value.startsWith(prevValue) ||
      !isReplayableComposedChar(char, prevValue.slice(-1))
    ) {
      return null;
    }

    const consumed = replayKeydown(e.target, char);
    if (consumed) {
      cancelImeRecommit(e.target, char);
    }
    return { consumed, value: `${doc.input.value}${char}` };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();

    const replay = replayAndroidComposedChar(e);
    if (replay?.consumed) {
      return;
    }

    const inputValue = normalizeSymbols(replay?.value ?? e.target.value);

    if (getIsAndroid() && e.target.value.endsWith(" ")) {
      splitInlineExpression(doc, e.target.value);
      return;
    }

    const matched = matchSubSupChar(inputValue);
    const value = matched?.value ?? inputValue;

    if (matched) {
      inputSubSup(matched.position)(doc);
    }

    if (doc.selection.hasSelectedNodes) {
      if (!matched && doc.selection.isContinuousSelection) {
        const operatorSuggestion = findSelectionApplyOperator(doc, value);
        if (operatorSuggestion) {
          handlePickSuggestion(doc, operatorSuggestion, { preserveHints: false });
          doc.editor.resetInput(true);
          return;
        }
      }

      doc.transact(() => {
        deleteSelectedNodes(doc);

        if ((doc.cursor.parentWrapperNode?.descendants.length || 0) <= 1) {
          removeEmptyInlineExpressionIfNeeded(doc, value);
        } else {
          doc.input.setValue(value);
          doc.editor.suggestions.mode = "active";
        }

        mergeInlineEditables(doc);
      }, "NodeKeyboardInput:deleteNodes");
    } else {
      doc.input.setValue(value);
      if (value.length === 0) {
        doc.editor.suggestions.reset();
      }
      doc.editor.hints.setPhantomHints([]);

      if (node?.isInlineEditable() && node.validateValue(value)) {
        doc.transact(() => {
          node.value = value;

          mergeInlineEditables(doc);
        }, "NodeKeyboardInput:immediateChange");
      } else if (!value) {
        doc.transact(() => {
          if (node?.isInlineEditable()) {
            deleteInlineEditableNode(node);
            removeEmptyInlineExpressionIfNeeded(doc);
          }
          mergeInlineEditables(doc);
        }, "NodeKeyboardInput:mergeInlineEditables");

        if (node?.isInlineEditable()) {
          doc.editor.resetInput(true);
        }
      }

      doc.selection.resetSelection();

      if (value.length > 0) {
        doc.editor.suggestions.mode = "active";
      }
    }
  };

  const handleSuggestionsPointerDown = useCallback(() => {
    doc.editor.suggestions.pointerTouch = true;
  }, [doc]);

  // pointerup fires before click, so clearing here lets the selection complete
  // before blur can dismiss the suggestion box on the next interaction
  const handleSuggestionsPointerUp = useCallback(() => {
    doc.editor.suggestions.pointerTouch = false;
  }, [doc]);

  const handleFocus = () => {
    setContextIsFocused(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    /**
     * The suggestions box takes focus for its own search input. Resetting here would
     * tear down the very menu the user is typing into.
     */
    if (e.relatedTarget?.closest(`[${SUGGESTIONS_MENU_ATTR}]`)) {
      return;
    }

    const { value } = doc.input;
    commitPendingInput(doc);
    if (value) {
      // input autoFocus can trigger re-focus of expression when pending input is commited
      // this is relevant when input is in uncommited state before user clicked somewhere outside of expression
      //
      // there should've been cursor reset, but for some reason, without moving cursor selection change is not registering
      //
      // consider using imperative approach for input focusing instead of relying on autoFocus
      doc.cursor.moveRight();
    }

    const wrapper = node?.ancestors.find((n) => n.is(WrapperNodeModel));

    if (wrapper && doc.nodes.has(wrapper.id) && wrapper.isEmpty && !wrapper.keepWhenEmpty) {
      doc.transact(() => {
        deleteNode(wrapper);
      }, "NodeKeyboardInput:blur");
    }

    doc.editor.resetInput(true);

    // Reset suggestion mode unless the user is interacting with the suggestion box.
    // On desktop, blur is prevented by e.preventDefault() in mousedown, so this only
    // runs when genuinely leaving focus. On mobile, the pointerTouch flag
    // keeps suggestions alive long enough for the tap selection to complete.
    if (getIsMobile() && !doc.editor.suggestions.pointerTouch) {
      doc.editor.suggestions.mode = "idle";
    }
    setContextIsFocused(false);
  };

  const { initCaretPosition } = doc.input;

  useEffect(() => {
    if (!isEditing || !initCaretPosition) return;
    const el = inputRef.current;
    if (el) {
      if ("from" in initCaretPosition) {
        const { from, to } = initCaretPosition;
        el.setSelectionRange(from, to);
      } else {
        const x = initCaretPosition.left - el.getBoundingClientRect().left;
        const pos = getCaretPositionFromCoordinates(el, x);
        if (pos === 0) {
          doc.editor.resetInput(true);
        } else {
          el.setSelectionRange(pos, pos);
        }
      }
    }
  }, [isEditing, doc, initCaretPosition]);

  const showSelected = doc.selection.hasSelectedNodes && !doc.selection.isPointerSelecting;
  const hasPointerNode = !!doc.editor.suggestions.pointerNode;

  const open =
    (contextIsFocused || doc.isFocused) &&
    doc.editor.suggestions.mode !== "idle" &&
    (doc.input.value.length > 0 || showSelected || hasPointerNode);

  const { suggestions } = doc.editor;
  const menuId = useId();
  // Focus stays in the input while the menu is open, so the input points at the active item.
  const activeOptionId =
    open && suggestions.visible
      ? suggestionOptionId(menuId, suggestions.nestedListId, suggestions.activeIndex)
      : undefined;
  // The submenu is portaled out of the menu, so it is named alongside the menu.
  const controlledIds = [
    menuId,
    suggestions.nestedListId ? suggestionSubmenuId(menuId, suggestions.nestedListId) : null,
  ]
    .filter(Boolean)
    .join(" ");

  const { refs, context } = useSuggestionsFloating({
    open,
    place: suggestionsRenderPlace,
    pointerAnchor: doc.editor.suggestions.pointerAnchor,
    onDismiss: () => doc.editor.suggestions.reset(),
  });

  const suggestionsRef = useRef<HTMLDivElement>(null);

  const { topSelectedNode } = doc.selection;

  useEffect(() => {
    let el = suggestionsRef.current;

    const { parent } = topSelectedNode ?? {};
    if (topSelectedNode && parent) {
      const cursorIndex = parent.nodes.findIndex((n) => n.id === topSelectedNode.id);
      if (cursorIndex !== -1) {
        const selector = `.${CURSOR_POSITION_CLASS_NAME}[data-node-id="${parent.id}"][data-cursor-index="${cursorIndex}"]`;
        const cursorEl = document.querySelector<HTMLDivElement>(selector);
        if (cursorEl?.parentElement) {
          el = cursorEl.parentElement as HTMLDivElement;
        }
      }
    }

    refs.setReference(el);
  }, [cursorContextId, topSelectedNode, refs]);

  const symbolNode = node && "symbolNode" in node ? node.symbolNode : undefined;
  const nodeValueChanged = isEditing && node && "value" in node && node.value !== doc.input.value;

  const symbolFont = symbolNode?.getFont(symbolFontContext);
  const fontSizeClassNames =
    !nodeValueChanged &&
    symbolFont &&
    twMerge(
      symbolFont.fontSize && `text-node-size-${symbolFont.fontSize}`,
      symbolFont.fontFeature && `font-${symbolFont.fontFeature}`,
    );

  return (
    <div
      className={twMerge("relative z-20 inline-block cursor-text", className)}
      onMouseOver={(e) => e.stopPropagation()}
      onFocus={() => {}}
    >
      <div className="absolute left-0 h-6 bottom-0 invisible" ref={suggestionsRef} />
      <Node.Dimensions
        dimension={inputDimensions}
        className={twMerge(
          "relative rounded-full bg-transparent",
          !doc.input.value && "px-0 min-w-0",
          doc.input.value && inputClassName,
          doc.input.value && "input-has-value",
        )}
      >
        {isEditing &&
          !disableEditingStyle &&
          (node?.isInlineEditable() ? (
            <div
              className={twMerge(
                "absolute left-0 bottom-0 w-full h-[1px] border-b border-edit-border",
                symbolPushClassName?.negative,
              )}
            />
          ) : (
            <div
              className={twMerge(
                "absolute left-0 top-0 w-full h-full z-1 rounded-full border border-edit-border border-dashed",
                isTextView && "rounded-none",
              )}
            />
          ))}
        <InputWithMath
          // While editing, the cursor can sit past the edited node, on the one after it.
          mathNode={doc.input.editingNode ?? cursor.nodeAfterCursor ?? cursor.nodeBeforeCursor}
          type="text"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect={showKeyboardSuggestions ? "on" : "off"}
          spellCheck={false}
          enterKeyHint="enter"
          data-1p-ignore
          data-lpignore="true"
          className={twMerge(
            "absolute z-2 left-0 top-0 w-full min-w-[1px] h-full whitespace-pre-wrap border-none bg-transparent text-center outline-none",
            symbolPushClassName?.negative,
            isParameterChild && "text-function-parameter",
            fontSizeClassNames,
          )}
          ref={inputRef}
          value={doc.input.value}
          onKeyDown={handleKeyDown}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleInputBlur}
          aria-autocomplete={activeOptionId ? "list" : undefined}
          aria-controls={activeOptionId ? controlledIds : undefined}
          aria-activedescendant={activeOptionId}
          data-testid="node-input"
          {...{ [MATH_INPUT_ATTR]: "" }}
        />
        <Node.Value
          className={twMerge(
            "flex w-2 -mx-1 whitespace-pre-wrap opacity-0",
            isEditing && node?.isInlineEditable() && "pointer-events-none",
            doc.input.value ? "w-auto mx-0" : "min-w-0",
            fontSizeClassNames,
          )}
        >
          {doc.input.value || <div className="inline-block selection:bg-transparent">&nbsp;</div>}
        </Node.Value>
      </Node.Dimensions>
      <FloatingPortal id={suggestionsAnchorId}>
        {open && (
          <Suggestions
            ref={refs.setFloating}
            id={menuId}
            value={doc.input.value}
            doc={doc}
            accept={accept}
            renderContext="math"
            floatingContext={context}
            onSelect={handleSelect}
            onPointerDown={handleSuggestionsPointerDown}
            onPointerUp={handleSuggestionsPointerUp}
            onPointerCancel={handleSuggestionsPointerUp}
          />
        )}
      </FloatingPortal>
    </div>
  );
});
